import React, { useState } from 'react';
import './TutorialModal.css';

const TUTORIAL_STORAGE_KEY = 'echomemoir_tutorial_completed';

const slides = [
  {
    id: 'welcome',
    icon: '🎙️',
    title: 'Welcome to EchoMemoir',
    subtitle: 'Your family story starts here',
    content: 'EchoMemoir helps you record, preserve, and share family stories across generations. This quick guide will show you the 6 key things you will do in the app.',
    tip: null
  },
  {
    id: 'circle',
    icon: '👨‍👩‍👧‍👦',
    title: 'Create a Family Circle',
    subtitle: 'Step 1 of 6',
    content: 'A Family Circle is your private space for a group of family members. Think of it as a digital family room where all your stories live together.',
    steps: [
      'Tap the plus Create New Circle card on your dashboard',
      'Give it a name like The Garcia Family or Grandma Stories',
      'You will be the Vault Keeper — the circle admin'
    ],
    tip: '💡 You can create multiple circles — one per branch of the family if you like.'
  },
  {
    id: 'record',
    icon: '🔴',
    title: 'Record a Story',
    subtitle: 'Step 2 of 6',
    content: 'Recording a story is as simple as tapping a button and talking. EchoMemoir automatically transcribes everything that is said.',
    steps: [
      'Open a Family Circle and tap the Record button',
      'Give the story a title, then tap the microphone to start',
      'Speak naturally — pause and resume anytime',
      'Tap Stop when done, then Save and Transcribe'
    ],
    tip: '💡 Max recording length is 90 minutes. You can add photos and documents to any story after saving.'
  },
  {
    id: 'enhance',
    icon: '✨',
    title: 'Enhance with AI',
    subtitle: 'Step 3 of 6',
    content: 'Once a story is transcribed, AI can transform it into beautifully written versions — perfect for a family memoir or history book.',
    steps: [
      'Open any story with a transcript',
      'Tap Enhance with AI',
      'Choose between Original, Polished, or Literary versions',
      'Non-English stories also get an English translation'
    ],
    tip: '💡 Each story can be enhanced up to 3 times. The original recording is always preserved.'
  },
  {
    id: 'video',
    icon: '🎬',
    title: 'Record Video Stories',
    subtitle: 'Step 4 of 6',
    content: 'Go beyond audio — record video stories so future generations can see and hear the people they love. Video stories are automatically transcribed just like audio.',
    steps: [
      'Open a Family Circle and tap the Record tab',
      'Toggle from Audio to Video at the top',
      'Tap the camera button to start — front camera is default',
      'Tap the flip icon to switch to rear camera anytime',
      'Tap Stop when done, then Save and Transcribe',
      'The video plays back in the story with full transcript below'
    ],
    tip: '💡 Video stories are available on Legacy Keeper and Legacy Vault plans. Max recording length is 10 minutes.'
  },
  {
    id: 'invite',
    icon: '📨',
    title: 'Invite Family Members',
    subtitle: 'Step 5 of 6',
    content: 'Stories are richer when the whole family contributes. Invite relatives to join your circle so they can listen, record their own stories, and add comments.',
    steps: [
      'Open a Family Circle and tap the Members tab',
      'Tap Invite Member and enter their email',
      'They will receive an email with a link to join',
      'New to the app? They will be guided to create a free account first'
    ],
    tip: '💡 You can assign roles: Vault Keeper (admin), Storyteller (can record), or Listener (read-only).'
  },
  {
    id: 'guests',
    icon: '💌',
    title: 'Collect Guest Memories',
    subtitle: 'Step 6 of 6',
    content: 'Hosting a birthday, wedding, reunion, or memorial? Generate a QR code link and let guests share memories instantly — no app or account needed.',
    steps: [
      'Open a Family Circle and tap the Memories tab',
      'Tap Generate Share Link and choose your event type',
      'Share the QR code or link with guests at your event',
      'Guests submit text, photos, or videos from any device',
      'You review and approve submissions before they are saved'
    ],
    tip: '💡 Approved memories are saved permanently to your circle. Guests receive a receipt email with a link to view their submission.'
  },
  {
    id: 'done',
    icon: '🎉',
    title: 'You are all set!',
    subtitle: 'Let us preserve some memories',
    content: 'That is everything you need to get started. Your family stories are waiting to be told — and EchoMemoir will keep them safe for generations to come.',
    tip: '💡 You can replay this tutorial anytime from the dashboard header.'
  }
];

const TutorialModal = ({ onClose }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState('next');

  const slide = slides[currentSlide];
  const isFirst = currentSlide === 0;
  const isLast = currentSlide === slides.length - 1;

  const goTo = (index, dir) => {
    if (animating) return;
    setDirection(dir || 'next');
    setAnimating(true);
    setTimeout(() => {
      setCurrentSlide(index);
      setAnimating(false);
    }, 220);
  };

  const handleNext = () => {
    if (isLast) {
      localStorage.setItem(TUTORIAL_STORAGE_KEY, 'true');
      onClose();
    } else {
      goTo(currentSlide + 1, 'next');
    }
  };

  const handlePrev = () => {
    if (!isFirst) goTo(currentSlide - 1, 'prev');
  };

  const handleSkip = () => {
    localStorage.setItem(TUTORIAL_STORAGE_KEY, 'true');
    onClose();
  };

  const getDotClass = (i) => {
    if (i === currentSlide) return 'tutorial-dot active';
    if (i < currentSlide) return 'tutorial-dot done';
    return 'tutorial-dot';
  };

  const getSlideClass = () => {
    if (animating) return 'tutorial-slide slide-exit-' + direction;
    return 'tutorial-slide slide-enter';
  };

  return (
    <div className="tutorial-overlay" onClick={handleSkip}>
      <div className="tutorial-modal" onClick={(e) => e.stopPropagation()}>

        <div className="tutorial-progress">
          {slides.map((_, i) => (
            <button
              key={i}
              className={getDotClass(i)}
              onClick={() => goTo(i, i > currentSlide ? 'next' : 'prev')}
            />
          ))}
        </div>

        <div className={getSlideClass()}>
          <div className="tutorial-icon">{slide.icon}</div>
          <div className="tutorial-subtitle">{slide.subtitle}</div>
          <h2 className="tutorial-title">{slide.title}</h2>
          <p className="tutorial-content">{slide.content}</p>

          {slide.steps && (
            <ul className="tutorial-steps">
              {slide.steps.map((step, i) => (
                <li key={i}>
                  <span className="step-num">{i + 1}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ul>
          )}

          {slide.tip && (
            <div className="tutorial-tip">{slide.tip}</div>
          )}
        </div>

        <div className="tutorial-nav">
          <button className="btn-tutorial-skip" onClick={handleSkip}>
            {isLast ? '' : 'Skip'}
          </button>
          <div className="tutorial-nav-right">
            {!isFirst && (
              <button className="btn-tutorial-prev" onClick={handlePrev}>
                Back
              </button>
            )}
            <button className="btn-tutorial-next" onClick={handleNext}>
              {isLast ? 'Get Started' : 'Next'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export { TUTORIAL_STORAGE_KEY };
export default TutorialModal;